import { bridgeName, command, nativeEventName } from "./NativeBridgeContract.generated";
import type { Subscription, Notification, NotificationAction } from "./types";

/** The Android-injected bridge object (window.NtfyAndroid). */
interface NativeBridgeObject {
  getCapabilities?: () => string;
  postMessage?: (message: string) => void;
  log?: (message: string) => void;
}

/** A native-event payload dispatched on `window` as a CustomEvent detail. */
interface NativeBridgeEvent {
  type?: string;
  payload?: { requestId?: string; message?: string; [key: string]: unknown };
}

interface PendingRequest {
  resolve: (value: NativeBridgeEvent["payload"]) => void;
  reject: (reason: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

const nativeBridgeObject = (): NativeBridgeObject | undefined =>
  (window as unknown as Record<string, NativeBridgeObject | undefined>)[bridgeName];

class NativeBridge {
  nextRequestId: number;

  pendingRequests: Map<string, PendingRequest>;

  constructor() {
    this.nextRequestId = 1;
    this.pendingRequests = new Map();
    if (typeof window !== "undefined") {
      window.addEventListener(nativeEventName, (event) => this.handleRequestEvent((event as CustomEvent).detail));
    }
  }

  isNativeApp() {
    return typeof window !== "undefined" && !!nativeBridgeObject();
  }

  getCapabilities() {
    const bridge = nativeBridgeObject();
    if (!this.isNativeApp() || typeof bridge?.getCapabilities !== "function") {
      return null;
    }
    try {
      return JSON.parse(bridge.getCapabilities());
    } catch (e) {
      console.error("[NativeBridge] Failed to parse native capabilities", e);
      return null;
    }
  }

  post(type: string, payload: Record<string, unknown> = {}) {
    const bridge = nativeBridgeObject();
    if (!this.isNativeApp() || typeof bridge?.postMessage !== "function") {
      return false;
    }
    bridge.postMessage(JSON.stringify({ type, payload }));
    return true;
  }

  request(type: string, payload: Record<string, unknown> = {}, timeoutMs = 30000): Promise<NativeBridgeEvent["payload"]> {
    if (!this.isNativeApp()) {
      return Promise.reject(new Error("Native bridge is not available"));
    }
    const requestId = `${Date.now()}-${this.nextRequestId}`;
    this.nextRequestId += 1;
    const posted = this.post(type, { ...payload, requestId });
    if (!posted) {
      return Promise.reject(new Error("Native bridge request failed"));
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Native bridge request timed out: ${type}`));
      }, timeoutMs);
      this.pendingRequests.set(requestId, { resolve, reject, timeout });
    });
  }

  handleRequestEvent(event: NativeBridgeEvent) {
    const requestId = event?.payload?.requestId;
    if (!requestId || !this.pendingRequests.has(requestId)) {
      return;
    }
    const pending = this.pendingRequests.get(requestId)!;
    this.pendingRequests.delete(requestId);
    clearTimeout(pending.timeout);
    if (event.type?.endsWith("Failed")) {
      pending.reject(new Error(event.payload?.message || event.type));
    } else {
      pending.resolve(event.payload);
    }
  }

  upsertSubscription(subscription: Subscription) {
    const payload = this.subscriptionPayload(subscription);
    return payload ? this.post(command.upsertSubscription, payload) : false;
  }

  updateSubscription(subscription: Subscription | null | undefined) {
    const payload = this.subscriptionPayload(subscription);
    return payload ? this.post(command.updateSubscription, payload) : false;
  }

  removeSubscription(subscription: Subscription) {
    const payload = this.subscriptionPayload(subscription);
    return payload ? this.post(command.removeSubscription, payload) : false;
  }

  syncSubscriptions(subscriptions: Subscription[] = []) {
    const payloads = subscriptions.map((subscription) => this.subscriptionPayload(subscription)).filter(Boolean);
    return this.post(command.syncSubscriptions, { subscriptions: payloads });
  }

  exportNotifications(sinceTime = 0) {
    return this.post(command.exportNotifications, { sinceTime });
  }

  markNotificationRead(notification: Notification | null | undefined) {
    const payload = this.notificationPayload(notification);
    return payload ? this.post(command.markNotificationRead, payload) : false;
  }

  markNotificationSequenceRead(subscriptionId: string, sequenceId: string) {
    return this.post(command.markNotificationSequenceRead, { subscriptionId, sequenceId });
  }

  markNotificationsRead(subscription: Subscription | null | undefined) {
    const payload = this.subscriptionPayload(subscription);
    return payload ? this.post(command.markNotificationsRead, payload) : false;
  }

  deleteNotification(notification: Notification | null | undefined) {
    const payload = this.notificationPayload(notification);
    return payload ? this.post(command.deleteNotification, payload) : false;
  }

  deleteNotificationSequence(subscriptionId: string, sequenceId: string) {
    return this.post(command.deleteNotificationSequence, { subscriptionId, sequenceId });
  }

  deleteNotifications(subscription: Subscription | null | undefined) {
    const payload = this.subscriptionPayload(subscription);
    return payload ? this.post(command.deleteNotifications, payload) : false;
  }

  performUserAction(notification: Notification, action: NotificationAction | null | undefined) {
    const payload = this.notificationPayload(notification);
    return payload && action?.id ? this.post(command.performUserAction, { ...payload, actionId: action.id }) : false;
  }

  openExternalUrl(url: string) {
    return url ? this.post(command.openExternalUrl, { url }) : false;
  }

  setPullRefreshEnabled(enabled: boolean) {
    return this.post(command.setPullRefreshEnabled, { enabled });
  }

  subscriptionPayload(subscription: Subscription | null | undefined) {
    if (!subscription || subscription.internal || !subscription.baseUrl || !subscription.topic) {
      return null;
    }
    return {
      baseUrl: subscription.baseUrl,
      topic: subscription.topic,
      displayName: subscription.displayName ?? null,
      mutedUntil: subscription.mutedUntil ?? 0,
      icon: subscription.icon ?? null, // 절대 URL — 앱이 중앙 세션 Bearer 로 받아 알림 큰 아이콘으로 쓴다
    };
  }


  notificationPayload(notification: Notification | null | undefined) {
    if (!notification?.id) {
      return null;
    }
    return {
      notificationId: notification.id,
      subscriptionId: notification.subscriptionId,
      sequenceId: notification.sequence_id || notification.sequenceId || notification.id,
    };
  }

  log(message: string) {
    const bridge = nativeBridgeObject();
    if (this.isNativeApp() && typeof bridge?.log === "function") {
      bridge.log(message);
    }
  }

  onEvent(handler: (detail: NativeBridgeEvent) => void) {
    const listener = (event: Event) => handler((event as CustomEvent).detail);
    window.addEventListener(nativeEventName, listener);
    return () => window.removeEventListener(nativeEventName, listener);
  }
}

const nativeBridge = new NativeBridge();
export default nativeBridge;
